import { useMemo, type CSSProperties } from 'react';
import { Drawer, DrawerBadge } from '@helios/ui/overlay';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qAnalysisRunImagesByRun,
  qAnalysisRunsByEncounter,
  type AiAnalysisRunImageRow,
  type AiAnalysisRunRow,
} from '@helios/data-access';

/* ------------------------------------------------------------------ */
/*  Dermatology AI Analysis Result Drawer (PRD.217 §7.3)              */
/*                                                                    */
/*  Renders the full result panel for an `ai_analysis_runs` row from  */
/*  the PowerSync mirror. raw_response_json is intentionally NOT      */
/*  synced and not shown — this view reads only the normalized JSON   */
/*  blobs (top_predictions, per_image_results, symptoms, treatment).  */
/* ------------------------------------------------------------------ */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  encounterId: string;
  /** If set, focuses this analysis run. Otherwise shows the most recent run for the encounter. */
  analysisRunId?: string | null;
}

interface TopPrediction {
  condition: string;
  confidence: number;
  severity: string;
}
interface PerImageResult {
  filename: string;
  diagnosis: string;
  confidence: number;
  severity: string;
}
interface SymptomsBlob {
  primary?: string[];
  secondary?: string[];
  lookAlike?: string[];
}
interface TreatmentBlob {
  treatmentClass?: string;
  commonAgents?: string[];
  redFlags?: string[];
  icd11?: string;
  disclaimer?: string;
}

function safeParse<T>(text: string | null | undefined): T | null {
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

export function DermatologyAnalysisDrawer({ open, onOpenChange, encounterId, analysisRunId }: Props) {
  const { data: runs = [] } = usePowerSyncQuery<AiAnalysisRunRow>(qAnalysisRunsByEncounter(encounterId));

  const run = useMemo<AiAnalysisRunRow | null>(() => {
    if (analysisRunId) return runs.find((r) => r.id === analysisRunId) ?? null;
    if (runs.length === 0) return null;
    // Fallback: most recent completed run; else most recent of any status.
    const completed = runs.find((r) => r.status === 'completed');
    return completed ?? runs[0];
  }, [runs, analysisRunId]);

  // Drawer was opened with a specific run id (we just hit Analyze and the
  // function returned this id) but the row hasn't reached the PowerSync local
  // mirror yet. The watched query above will auto-refire when it arrives.
  const awaitingSync = analysisRunId != null && !run;

  const { data: imageLinks = [] } = usePowerSyncQuery<AiAnalysisRunImageRow>(
    qAnalysisRunImagesByRun(run?.id ?? ''),
  );

  const topPredictions = safeParse<TopPrediction[]>(run?.top_predictions_json) ?? [];
  const perImageResults = safeParse<PerImageResult[]>(run?.per_image_results_json) ?? [];
  const symptoms = safeParse<SymptomsBlob>(run?.symptoms_json) ?? {};
  const treatment = safeParse<TreatmentBlob>(run?.treatment_json) ?? {};

  const headerBadge =
    run?.status === 'completed' ? (
      <DrawerBadge label="AI Suggested" variant="neutral" />
    ) : run?.status === 'failed' ? (
      <DrawerBadge label="Failed" variant="warning" />
    ) : run?.status === 'pending' ? (
      <DrawerBadge label="Analyzing…" variant="neutral" />
    ) : null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Dermatology AI Analysis"
      description={
        run?.completed_at
          ? `Completed ${new Date(run.completed_at).toLocaleString()}`
          : run?.requested_at
            ? `Requested ${new Date(run.requested_at).toLocaleString()}`
            : awaitingSync
              ? 'Syncing the analysis result to this device…'
              : 'No analysis runs yet for this encounter.'
      }
      width={520}
      badges={headerBadge ?? (awaitingSync ? <DrawerBadge label="Syncing…" variant="neutral" /> : undefined)}
    >
      {!run ? (
        awaitingSync ? <SyncingBlock /> : <Empty />
      ) : run.status === 'failed' ? (
        <FailedBlock errorMessage={run.error_message ?? null} />
      ) : run.status === 'pending' ? (
        <PendingBlock />
      ) : (
        <div style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
          <Section title="Primary diagnosis">
            <div style={styles.primary}>{run.primary_diagnosis || '—'}</div>
            <div style={styles.subMeta}>
              {run.confidence != null && (
                <span>Confidence {formatPct(run.confidence)}</span>
              )}
              {run.severity && <span>Severity {run.severity}</span>}
              {run.agreement != null && imageLinks.length > 1 && (
                <span>Agreement {formatPct(run.agreement)}</span>
              )}
            </div>
          </Section>

          {topPredictions.length > 0 && (
            <Section title="Top predictions">
              <ul style={styles.bareList}>
                {topPredictions.map((p, i) => (
                  <li key={i} style={styles.row}>
                    <span style={styles.rowLabel}>{p.condition}</span>
                    <span style={styles.rowMeta}>
                      {formatPct(p.confidence)} · {p.severity}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {perImageResults.length > 0 && (
            <Section title="Per-image results">
              <ul style={styles.bareList}>
                {perImageResults.map((p, i) => (
                  <li key={i} style={styles.row}>
                    <span style={styles.rowLabel}>{p.filename}</span>
                    <span style={styles.rowMeta}>
                      {p.diagnosis} · {formatPct(p.confidence)} · {p.severity}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(symptoms.primary?.length || symptoms.secondary?.length || symptoms.lookAlike?.length) ? (
            <Section title="Symptoms">
              {symptoms.primary?.length ? (
                <SymptomGroup label="Primary" items={symptoms.primary} />
              ) : null}
              {symptoms.secondary?.length ? (
                <SymptomGroup label="Secondary" items={symptoms.secondary} />
              ) : null}
              {symptoms.lookAlike?.length ? (
                <SymptomGroup label="Look-alike" items={symptoms.lookAlike} />
              ) : null}
            </Section>
          ) : null}

          {(treatment.treatmentClass ||
            treatment.commonAgents?.length ||
            treatment.redFlags?.length ||
            treatment.icd11) && (
            <Section title="Treatment reference">
              <p style={styles.referenceNote}>
                Reference only. Final treatment decisions are the clinician&apos;s responsibility.
              </p>
              {treatment.treatmentClass && (
                <div style={styles.kvRow}>
                  <span style={styles.kvKey}>Class</span>
                  <span style={styles.kvVal}>{treatment.treatmentClass}</span>
                </div>
              )}
              {treatment.icd11 && (
                <div style={styles.kvRow}>
                  <span style={styles.kvKey}>ICD-11</span>
                  <span style={styles.kvVal}>{treatment.icd11}</span>
                </div>
              )}
              {treatment.commonAgents?.length ? (
                <SymptomGroup label="Common agents" items={treatment.commonAgents} />
              ) : null}
              {treatment.redFlags?.length ? (
                <SymptomGroup label="Red flags" items={treatment.redFlags} warning />
              ) : null}
              {treatment.disclaimer && (
                <p style={styles.disclaimer}>{treatment.disclaimer}</p>
              )}
            </Section>
          )}

          {(run.disclaimer || run.model_version) && (
            <Section title="Provider">
              <div style={styles.subMeta}>
                {run.provider && <span>{run.provider}</span>}
                {run.model_version && <span>Model {run.model_version}</span>}
                {run.provider_mode && <span>Mode {run.provider_mode}</span>}
              </div>
              {run.disclaimer && <p style={styles.disclaimer}>{run.disclaimer}</p>}
              <p style={styles.disclaimer}>
                AI analysis is for clinical support only. Final diagnosis and treatment decisions
                must be made by the clinician.
              </p>
            </Section>
          )}
        </div>
      )}
    </Drawer>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function SymptomGroup({
  label,
  items,
  warning,
}: {
  label: string;
  items: string[];
  warning?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={styles.groupLabel}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((item, i) => (
          <span
            key={i}
            style={{
              ...styles.pill,
              background: warning ? '#fee2e2' : 'var(--bg-surface-2, #f3f4f6)',
              color: warning ? '#991b1b' : 'var(--text, #111827)',
            }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function Empty() {
  return (
    <div style={{ padding: 'var(--sp-8)', textAlign: 'center', color: 'var(--text-muted)' }}>
      No AI analysis run yet for this encounter. Select 1–5 images and click <strong>Analyze</strong> to start.
    </div>
  );
}

function PendingBlock() {
  return (
    <div style={{ padding: 'var(--sp-8)', textAlign: 'center', color: 'var(--text-muted)' }}>
      Analysis is in progress…
    </div>
  );
}

function SyncingBlock() {
  return (
    <div
      style={{
        padding: 'var(--sp-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        alignItems: 'center',
        textAlign: 'center',
        color: 'var(--text-muted)',
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--text)' }}>Analysis complete</div>
      <div style={{ fontSize: 'var(--font-size-sm)' }}>
        Syncing results to this device. This usually takes a couple of seconds — the panel will
        update automatically.
      </div>
    </div>
  );
}

function FailedBlock({ errorMessage }: { errorMessage: string | null }) {
  return (
    <div style={{ padding: 'var(--sp-6)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div style={{ fontWeight: 600, color: 'var(--text)' }}>AI analysis failed</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
        {errorMessage ?? 'The provider could not complete this analysis. Please try again or review the image manually.'}
      </div>
    </div>
  );
}

// ─── Formatting ─────────────────────────────────────────────────────────────

function formatPct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  // Siksha returns 0–100 (sometimes 0–1 for `agreement`); normalize the
  // common 0–1 case heuristically.
  const pct = n > 1 ? n : n * 100;
  return `${Math.round(pct * 10) / 10}%`;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  primary: {
    fontSize: 'var(--font-size-lg, 16px)',
    fontWeight: 600,
    color: 'var(--text, #111827)',
  },
  subMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--sp-3, 12px)',
    fontSize: 'var(--font-size-sm, 12px)',
    color: 'var(--text-muted, #6b7280)',
  },
  sectionTitle: {
    margin: 0,
    fontSize: 'var(--font-size-xs, 11px)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted, #6b7280)',
  },
  bareList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-2, 8px)',
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--sp-3, 12px)',
  },
  rowLabel: {
    fontSize: 'var(--font-size-sm, 12px)',
    color: 'var(--text, #111827)',
    fontWeight: 500,
  },
  rowMeta: {
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-muted, #6b7280)',
  },
  groupLabel: {
    fontSize: 'var(--font-size-xs, 11px)',
    fontWeight: 500,
    color: 'var(--text-muted, #6b7280)',
  },
  pill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full, 9999px)',
    fontSize: 'var(--font-size-xs, 11px)',
    lineHeight: '18px',
  },
  kvRow: {
    display: 'flex',
    gap: 'var(--sp-3, 12px)',
    fontSize: 'var(--font-size-sm, 12px)',
  },
  kvKey: {
    color: 'var(--text-muted, #6b7280)',
    width: 80,
    flexShrink: 0,
  },
  kvVal: {
    color: 'var(--text, #111827)',
    fontWeight: 500,
  },
  referenceNote: {
    margin: 0,
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-muted, #6b7280)',
    fontStyle: 'italic',
  },
  disclaimer: {
    margin: 0,
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-muted, #6b7280)',
    lineHeight: 1.5,
  },
};
